import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sayfanın başlık çubuğunu (<header>) bulur; rozet oraya portal ile yerleşir, açık panel hemen altına açılır
const usePageHeader = (pathname) => {
  const ref = useRef(null);
  const [headerEl, setHeaderEl] = useState(null);
  const [top, setTop] = useState(72);
  useEffect(() => {
    const sync = () => {
      const h = document.querySelector('header');
      if (h && getComputedStyle(h).position === 'static') h.style.position = 'relative';
      // Rozet için başlığın sağında 28px yer aç (Çıkış vb. düğmelerle çakışmasın) — bir kez uygulanır
      if (h && !h.dataset.notifPad) { h.dataset.notifPad = '1'; h.style.paddingRight = `${parseFloat(getComputedStyle(h).paddingRight) + 28}px`; }
      if (ref.current !== h) { ref.current = h; setHeaderEl(h || null); }
      const bottom = h ? h.getBoundingClientRect().bottom : 0;
      setTop(bottom > 0 ? Math.round(bottom) + 8 : 12);
    };
    sync();
    const mo = new MutationObserver(() => { if (document.querySelector('header') !== ref.current) sync(); });
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', sync);
    return () => { mo.disconnect(); window.removeEventListener('resize', sync); };
  }, [pathname]);
  return { headerEl, top };
};

const TaskNotificationPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { headerEl, top } = usePageHeader(pathname);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (user && !loaded) {
      fetchNotifications();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/my-task-notifications`, { withCredentials: true });
      const tasks = res.data;
      const now = new Date();
      const items = [];

      tasks.forEach(task => {
        const due = task.due_date ? new Date(task.due_date) : null;
        if (!due) return;

        const diffMs = due - now;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
          // Gecikmiş
          items.push({
            id: task.id,
            title: task.title,
            type: 'overdue',
            days: Math.abs(diffDays),
            message: `${Math.abs(diffDays)} gün gecikmiş!`,
            color: 'red',
            priority: task.priority
          });
        } else if (diffDays === 0) {
          // Bugün
          items.push({
            id: task.id,
            title: task.title,
            type: 'today',
            days: 0,
            message: 'Bugün son gün!',
            color: 'red',
            priority: task.priority
          });
        } else if (diffDays <= 3) {
          // 3 gün içinde
          items.push({
            id: task.id,
            title: task.title,
            type: 'urgent',
            days: diffDays,
            message: `${diffDays} gün kaldı`,
            color: 'amber',
            priority: task.priority
          });
        } else if (diffDays <= 7) {
          // 1 hafta içinde
          items.push({
            id: task.id,
            title: task.title,
            type: 'soon',
            days: diffDays,
            message: `${diffDays} gün kaldı`,
            color: 'blue',
            priority: task.priority
          });
        }
      });

      // Önce gecikmiş, sonra bugün, sonra acil, sonra yakın
      items.sort((a, b) => {
        const order = { overdue: 0, today: 1, urgent: 2, soon: 3 };
        return (order[a.type] || 4) - (order[b.type] || 4);
      });

      setNotifications(items);
      setLoaded(true);
      // Varsayılan olarak rozet (küçültülmüş) halde göster. Kullanıcı tıklayarak açar.
    } catch (error) {
      console.error('Bildirim hatası:', error);
      setLoaded(true);
    }
  };

  if (!user || notifications.length === 0) return null;

  const overdueCount = notifications.filter(n => n.type === 'overdue').length;
  const todayCount = notifications.filter(n => n.type === 'today').length;
  const urgentCount = notifications.filter(n => n.type === 'urgent' || n.type === 'soon').length;

  // Küçültülmüş hali - başlık çubuğunun en sağ boşluğunda küçük rozet (içerik düğmeleriyle çakışmaz)
  if (minimized || !open) {
    const badge = (
      <button
        onClick={() => { setOpen(true); setMinimized(false); }}
        style={headerEl ? undefined : { top }}
        className={`${headerEl ? 'absolute right-[3px] top-1/2 -translate-y-1/2' : 'fixed right-1'} z-40 w-[22px] h-[22px] rounded-full bg-gray-800 border border-amber-500/70 shadow flex items-center justify-center hover:bg-gray-700 transition-colors`}
        title={`${notifications.length} görev hatırlatması${overdueCount ? ` · ${overdueCount} gecikmiş` : ''}`}
        data-testid="notification-badge"
      >
        <span className="text-amber-300 text-[10px] font-bold leading-none" data-testid="notification-badge-count">{notifications.length}</span>
        {overdueCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500" data-testid="notification-badge-overdue" />
        )}
      </button>
    );
    return headerEl ? createPortal(badge, headerEl) : badge;
  }

  // Açık panel
  return (
    <div style={{ top }} className="fixed right-4 z-40 w-80 max-h-96 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl flex flex-col" data-testid="notification-panel">
      {/* Başlık */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span className="text-white text-sm font-semibold">Görev Hatırlatmaları</span>
          <span className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full">{notifications.length}</span>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setMinimized(true)} className="text-gray-500 hover:text-gray-300 p-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" /></svg>
          </button>
          <button onClick={() => { setOpen(false); setMinimized(true); }} className="text-gray-500 hover:text-gray-300 p-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>

      {/* Özet */}
      <div className="flex gap-2 px-4 py-2 border-b border-gray-800">
        {overdueCount > 0 && <span className="text-xs bg-red-900/50 text-red-300 px-2 py-0.5 rounded">{overdueCount} gecikmiş</span>}
        {todayCount > 0 && <span className="text-xs bg-red-900/50 text-red-300 px-2 py-0.5 rounded">{todayCount} bugün</span>}
        {urgentCount > 0 && <span className="text-xs bg-amber-900/50 text-amber-300 px-2 py-0.5 rounded">{urgentCount} yaklaşan</span>}
      </div>

      {/* Liste */}
      <div className="overflow-y-auto flex-1 px-2 py-2 space-y-1.5">
        {notifications.map(n => (
          <button
            key={n.id}
            onClick={() => navigate('/tasks')}
            className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors hover:bg-gray-800/70 ${
              n.type === 'overdue' ? 'bg-red-950/30 border border-red-900/50' :
              n.type === 'today' ? 'bg-red-950/20 border border-red-900/30' :
              n.type === 'urgent' ? 'bg-amber-950/20 border border-amber-900/30' :
              'bg-gray-800/30 border border-gray-800'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-white truncate flex-1">{n.title}</p>
              <span className={`text-xs font-bold whitespace-nowrap ${
                n.type === 'overdue' || n.type === 'today' ? 'text-red-400' :
                n.type === 'urgent' ? 'text-amber-400' : 'text-blue-400'
              }`}>{n.message}</span>
            </div>
            {n.priority && (
              <span className={`text-xs mt-1 inline-block ${
                n.priority === 'urgent' ? 'text-red-500' :
                n.priority === 'high' ? 'text-orange-400' : 'text-gray-500'
              }`}>
                {n.priority === 'urgent' ? 'Acil' : n.priority === 'high' ? 'Yüksek' : n.priority === 'medium' ? 'Normal' : 'Düşük'}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Alt */}
      <div className="px-4 py-2 border-t border-gray-800">
        <button onClick={() => navigate('/tasks')} className="text-xs text-blue-400 hover:text-blue-300">
          Tüm görevleri görüntüle
        </button>
      </div>
    </div>
  );
};

export default TaskNotificationPanel;
